import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Tickets from "./pages/Tickets";
import TicketDetail from "./pages/TicketDetail";
import CreateTicket from "./pages/CreateTicket";
import Kanban from "./pages/Kanban";
import Users from "./pages/Users";
import KnowledgeBase from "./pages/KnowledgeBase";
import Documentation from "./pages/Documentation";
import ArticleDetail from "./pages/ArticleDetail";
import WallView from "./pages/WallView";
import Settings from "./pages/Settings";
import PublicPortal from "./pages/PublicPortal";
import Satisfaction from "./pages/Satisfaction";
import Contracts from "./pages/Contracts";
import ContractStats from "./pages/ContractStats";
import Companies from "./pages/Companies";
import CompanyDetail from "./pages/CompanyDetail";
import Equipment from "./pages/Equipment";
import Macros from "./pages/Macros";
import AutoResponses from "./pages/AutoResponses";
import Escalations from "./pages/Escalations";
import MySignature from "./pages/MySignature";
import MesServices from "./pages/MesServices";
import KBTrash from "./pages/KBTrash";
import Favorites from "./pages/Favorites";
import TechStats from "./pages/TechStats";
import IaNetkom from "./pages/IaNetkom";
import BugReports from "./pages/BugReports";
import AboutPage from "./pages/AboutPage";
import Modules from "./pages/Modules";
import CyberAudit from "./pages/CyberAudit";
import DnsGenerator from "./pages/DnsGenerator";
import Phishing from "./pages/Phishing";
import SoftwareLicenses from "./pages/SoftwareLicenses";
import WebHosting from "./pages/WebHosting";
import ImapMigration from "./pages/ImapMigration";
import SocialMedia from "./pages/SocialMedia";
import Sav from "./pages/Sav";
import PurchaseOrders from "./pages/PurchaseOrders";
import WeeklyDigestSettings from "./pages/WeeklyDigestSettings";
import Newsletter from "./pages/Newsletter";
import WhatsAppStats from "./pages/WhatsAppStats";
import TranslationModule from "./pages/TranslationModule";
import WallModule from "./pages/WallModule";
import "./App.css";

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Chargement…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Chargement…</div>;
  if (!user) return <Navigate to="/portail" replace />;
  return <Navigate to={user.role === "client" ? "/mes-tickets" : "/dashboard"} replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={<Login />} />
          <Route path="/portail" element={<PublicPortal />} />
          <Route path="/satisfaction" element={<Satisfaction />} />
          <Route path="/mur-public" element={<WallView />} />
          {/* v279 — Authenticated wall view for staff. Techs clicking the
              "Mur des urgences" module card land here (see Modules.jsx). */}
          <Route path="/mur" element={<Protected roles={["admin", "technicien"]}><WallView /></Protected>} />

          {/* Staff routes */}
          <Route path="/dashboard" element={<Protected roles={["admin", "technicien"]}><Dashboard /></Protected>} />
          <Route path="/tickets" element={<Protected roles={["admin", "technicien"]}><Tickets /></Protected>} />
          <Route path="/tickets/nouveau" element={<Protected><CreateTicket /></Protected>} />
          <Route path="/tickets/:id" element={<Protected><TicketDetail /></Protected>} />
          <Route path="/kanban" element={<Protected roles={["admin", "technicien"]}><Kanban /></Protected>} />
          <Route path="/inventaire" element={<Protected><Equipment /></Protected>} />
          <Route path="/macros" element={<Protected roles={["admin", "technicien"]}><Macros /></Protected>} />
          <Route path="/ia-netkom" element={<Protected roles={["admin", "technicien"]}><IaNetkom /></Protected>} />
          <Route path="/favoris" element={<Protected roles={["admin", "technicien"]}><Favorites /></Protected>} />
          <Route path="/automations" element={<Protected roles={["admin"]}><AutoResponses /></Protected>} />
          <Route path="/escalades" element={<Protected roles={["admin"]}><Escalations /></Protected>} />
          <Route path="/ma-signature" element={<Protected roles={["admin","technicien"]}><MySignature /></Protected>} />
          <Route path="/contrats" element={<Protected roles={["admin", "technicien"]}><Contracts /></Protected>} />
          <Route path="/contrats/:id/stats" element={<Protected roles={["admin", "technicien"]}><ContractStats /></Protected>} />
          <Route path="/societes" element={<Protected roles={["admin", "technicien"]}><Companies /></Protected>} />
          <Route path="/societes/:id" element={<Protected roles={["admin", "technicien"]}><CompanyDetail /></Protected>} />
          <Route path="/utilisateurs" element={<Protected roles={["admin", "technicien"]}><Users /></Protected>} />
          <Route path="/statistiques-techs" element={<Protected roles={["admin", "technicien"]}><TechStats /></Protected>} />
          <Route path="/parametres" element={<Protected roles={["admin"]}><Settings /></Protected>} />
          <Route path="/modules" element={<Protected roles={["admin", "technicien"]}><Modules /></Protected>} />
          <Route path="/newsletter" element={<Protected roles={["admin"]}><Newsletter /></Protected>} />
          <Route path="/modules/cyber-audit" element={<Protected roles={["admin", "technicien"]}><CyberAudit /></Protected>} />
          <Route path="/modules/dns-generator" element={<Protected roles={["admin", "technicien"]}><DnsGenerator /></Protected>} />
          <Route path="/modules/phishing-sim" element={<Protected roles={["admin", "technicien"]}><Phishing /></Protected>} />
          {/* v280 — techs peuvent aussi accéder au module Langues (lecture
              seule sur le moteur IA / la clé Google, mais ils peuvent
              sélectionner les langues actives et voir la consommation). */}
          <Route path="/modules/translation" element={<Protected roles={["admin", "technicien"]}><TranslationModule /></Protected>} />
          <Route path="/modules/wall" element={<Protected roles={["admin"]}><WallModule /></Protected>} />
          <Route path="/modules/software-licenses" element={<Protected roles={["admin", "technicien"]}><SoftwareLicenses /></Protected>} />
          <Route path="/modules/web-hosting" element={<Protected roles={["admin", "technicien"]}><WebHosting /></Protected>} />
          <Route path="/modules/imap-migration" element={<Protected roles={["admin", "technicien"]}><ImapMigration /></Protected>} />
          <Route path="/modules/social-media" element={<Protected roles={["admin", "technicien"]}><SocialMedia /></Protected>} />
          <Route path="/modules/sav" element={<Protected roles={["admin", "technicien"]}><Sav /></Protected>} />
          <Route path="/modules/purchase-orders" element={<Protected roles={["admin", "technicien"]}><PurchaseOrders /></Protected>} />
          <Route path="/parametres/retro-hebdo" element={<Protected roles={["admin"]}><WeeklyDigestSettings /></Protected>} />
          <Route path="/modules/whatsapp-stats" element={<Protected roles={["admin", "technicien"]}><WhatsAppStats /></Protected>} />
          <Route path="/signaler-un-bug" element={<Protected roles={["admin", "technicien"]}><BugReports /></Protected>} />
          <Route path="/a-propos" element={<Protected roles={["admin", "technicien"]}><AboutPage /></Protected>} />

          {/* Client routes */}
          <Route path="/mes-tickets" element={<Protected><Tickets /></Protected>} />
          <Route path="/mes-tickets/:id" element={<Protected><TicketDetail /></Protected>} />
          <Route path="/nouvelle-demande" element={<Protected><CreateTicket /></Protected>} />
          <Route path="/mes-services" element={<Protected roles={["client"]}><MesServices /></Protected>} />
          <Route path="/clients/:id/services" element={<Protected roles={["admin", "technicien"]}><MesServices /></Protected>} />

          {/* Shared */}
          <Route path="/kb" element={<Protected><KnowledgeBase /></Protected>} />
          <Route path="/kb/corbeille" element={<Protected roles={["admin"]}><KBTrash /></Protected>} />
          <Route path="/kb/:id" element={<Protected><ArticleDetail /></Protected>} />
          <Route path="/documentation" element={<Protected><Documentation /></Protected>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
